import React from 'react';
import { motion } from 'framer-motion';
import { Star, Target, TrendingUp, CheckCircle2, AlertCircle, BookOpen, BarChart2, Clock, Zap, Sparkles, Play, ChevronLeft, Info, Lightbulb, Activity, Award } from 'lucide-react';
import type { AssessmentReport } from '../types';
import type { ModuleId } from '../../content';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
const MAX_TIER = 16;
const MIN_QUESTIONS_FOR_ESTIMATE = 3;
interface AssessmentReportPageProps {
  report: AssessmentReport;
  onExit: () => void;
  onRetake?: () => void;
  onPracticeWeakAreas?: () => void;
  onStartPracticeForModule?: (moduleId: ModuleId) => void;
}
const stagger = (i: number) => ({
  delay: 0.06 * i
});
const fadeUp = {
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.35,
    ease: 'easeOut'
  }
};
export function AssessmentReportPage({
  report,
  onExit,
  onRetake,
  onPracticeWeakAreas,
  onStartPracticeForModule
}: AssessmentReportPageProps) {
  const {
    score,
    time,
    byTier,
    estimatedLevels,
    overallEstimatedLevelCode,
    overallExpectedLevelCode,
    overallEstimatedTier,
    overallExpectedTier,
    growthIndicators,
    actionPlan,
    strengthsBySkill,
    focusAreasBySkill,
    strongAreas,
    weakAreas,
    suggestedPractice,
    recommendation
  } = report;
  const formatDate = (ts: number) => new Date(ts).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const hasAnalysis = strengthsBySkill.length > 0 || focusAreasBySkill.length > 0 || strongAreas.length > 0 || weakAreas.length > 0;
  const hasActionPlan = actionPlan.beginMet || actionPlan.gaVerderMet.length > 0 || actionPlan.uitdaging;
  const hasTierData = byTier.length > 0;
  return <div className="min-h-screen bg-slate-50 font-sans flex flex-col" data-id="element-957">
      {/* Minimal Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200/80 px-4 py-2.5 flex items-center justify-between" data-id="element-958">
        <button onClick={onExit} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 font-medium transition-colors text-sm" aria-label="Terug naar dashboard" data-id="element-959">
          <ChevronLeft className="w-5 h-5" data-id="element-960" />
          <span className="hidden sm:inline" data-id="element-961">Terug</span>
        </button>
        <h1 className="text-sm font-bold text-slate-700 tracking-tight" data-id="element-962">
          Leerlingprofiel
        </h1>
        <div className="w-14" data-id="element-963" />
      </header>

      {/* Scrollable Content */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-6" data-id="element-964">
        {/* 1. Overall Result Hero */}
        <motion.section {...fadeUp} className="relative overflow-hidden rounded-2xl bg-indigo-700 text-white shadow-lg" data-id="element-965">
          <div className="absolute -top-8 -right-8 opacity-[0.06]" data-id="element-966">
            <Award className="w-56 h-56" data-id="element-967" />
          </div>

          <div className="relative px-5 pt-5 pb-4 sm:px-7 sm:pt-7 sm:pb-5 flex flex-col sm:flex-row items-center gap-5" data-id="element-968">
            {/* Left: Level + Context */}
            <div className="flex-1 text-center sm:text-left" data-id="element-969">
              <p className="text-indigo-200 text-sm font-medium mb-1" data-id="element-970">
                Groep {report.selectedGroup} · {formatDate(report.completedAt)}
              </p>
              <div className="flex items-baseline justify-center sm:justify-start gap-2.5 mb-1.5" data-id="element-971">
                <h2 className="text-4xl sm:text-5xl font-black tracking-tight leading-none" data-id="element-972">
                  {overallEstimatedLevelCode}
                </h2>
                <span className="text-indigo-300 font-medium text-base" data-id="element-973">
                  / {overallExpectedLevelCode}
                </span>
              </div>
              <p className="text-indigo-100 text-sm sm:text-base font-medium max-w-sm leading-snug" data-id="element-974">
                {overallEstimatedTier === overallExpectedTier ? 'Je zit precies op het verwachte niveau. Keurig gewerkt!' : overallEstimatedTier > overallExpectedTier ? 'Uitstekend! Je scoort boven het verwachte niveau.' : 'Je bent goed op weg, maar er is nog ruimte voor groei.'}
              </p>
            </div>

            {/* Right: Score Ring */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 relative flex-shrink-0" data-id="element-975">
              <svg className="w-full h-full transform -rotate-90" data-id="element-976">
                <circle cx="50%" cy="50%" r="42%" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" data-id="element-977" />
                <motion.circle cx="50%" cy="50%" r="42%" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" initial={{
                pathLength: 0
              }} animate={{
                pathLength: score.percentage / 100
              }} transition={{
                duration: 1.2,
                ease: 'easeOut'
              }} data-id="element-978" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-979">
                <span className="text-2xl sm:text-3xl font-black leading-none" data-id="element-980">
                  {score.percentage}%
                </span>
                <span className="text-[9px] font-bold text-indigo-200 uppercase tracking-widest mt-0.5" data-id="element-981">
                  Score
                </span>
              </div>
            </div>
          </div>

          {/* Stats Strip */}
          <div className="bg-indigo-900/30 px-5 py-3 grid grid-cols-3 text-center text-sm divide-x divide-indigo-500/30" data-id="element-982">
            <div data-id="element-983">
              <span className="text-indigo-300 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-984">
                Goed
              </span>
              <span className="font-bold" data-id="element-985">
                {score.correctCount}/{score.totalQuestions}
              </span>
            </div>
            <div data-id="element-986">
              <span className="text-indigo-300 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-987">
                Tijd
              </span>
              <span className="font-bold" data-id="element-988">{formatTime(time.totalSeconds)}</span>
            </div>
            <div data-id="element-989">
              <span className="text-indigo-300 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-990">
                Per vraag
              </span>
              <span className="font-bold" data-id="element-991">
                {Math.round(time.avgPerQuestion)}s
              </span>
            </div>
          </div>
        </motion.section>

        {/* 2. Level per Subject */}
        <motion.section {...fadeUp} transition={stagger(1)} className="space-y-3" data-id="element-992">
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-993">
            <BarChart2 className="w-4.5 h-4.5 text-slate-500" data-id="element-994" />
            Niveau per Vakgebied
          </h3>
          <Card className="overflow-hidden shadow-sm" data-id="element-995">
            <div className="divide-y divide-slate-100" data-id="element-996">
              {estimatedLevels.map(level => {
              const growth = growthIndicators.find(g => g.moduleId === level.moduleId);
              const isUnknown = level.questionCount < MIN_QUESTIONS_FOR_ESTIMATE;
              return <div key={level.moduleId} className="px-4 py-3.5 sm:px-5" data-id="element-997">
                    <div className="flex items-center gap-3 mb-2" data-id="element-998">
                      <span className="w-28 sm:w-36 font-bold text-slate-900 text-sm truncate" data-id="element-999">
                        {level.moduleTitle}
                      </span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden relative" data-id="element-1000">
                        <div className="absolute top-0 bottom-0 w-px bg-slate-400 z-10" style={{
                      left: `${level.expectedTier / MAX_TIER * 100}%`
                    }} data-id="element-1001" />
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${level.estimatedTier / MAX_TIER * 100}%`
                    }} transition={{
                      duration: 0.8,
                      ease: 'easeOut'
                    }} className={`h-full rounded-full ${isUnknown ? 'bg-slate-300' : level.delta > 0 ? 'bg-emerald-500' : level.delta === 0 ? 'bg-blue-500' : level.delta >= -1 ? 'bg-yellow-400' : 'bg-amber-500'}`} data-id="element-1002" />
                      </div>
                      <span className="w-12 text-right font-black text-slate-800 text-sm" data-id="element-1003">
                        {isUnknown ? '?' : level.estimatedLevelCode}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs pl-0 sm:pl-[7.5rem] sm:ml-3" data-id="element-1004">
                      <span className="flex items-center gap-1.5 text-slate-500" data-id="element-1005">
                        {growth && <span className={`w-1.5 h-1.5 rounded-full inline-block ${['groei_snel', 'gaat_goed', 'ik_groei'].includes(growth.level) ? 'bg-emerald-500' : ['let_op', 'extra_aandacht'].includes(growth.level) ? 'bg-amber-500' : ['krimpt', 'actie_vereist'].includes(growth.level) ? 'bg-red-500' : 'bg-slate-300'}`} data-id="element-1006" />}
                        {growth?.label || 'Te weinig data'}
                      </span>
                      <span className="text-slate-400" data-id="element-1007">
                        {level.accuracy}% · {level.questionCount} vr.
                      </span>
                    </div>
                  </div>;
            })}
            </div>
            <div className="bg-slate-50 px-4 py-2.5 border-t border-slate-100 text-xs text-slate-400 flex items-start gap-1.5" data-id="element-1008">
              <Info className="w-3.5 h-3.5 mt-px flex-shrink-0" data-id="element-1009" />
              <span data-id="element-1010">
                Streepje = verwacht niveau groep {report.selectedGroup}. Balk =
                gemeten niveau.
              </span>
            </div>
          </Card>
        </motion.section>

        {/* 3. Tier Breakdown & Time (side by side) */}
        {(hasTierData || time.totalSeconds > 0) && <motion.section {...fadeUp} transition={stagger(2)} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1011">
            {hasTierData && <Card className="p-4 shadow-sm" data-id="element-1012">
                <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm mb-3" data-id="element-1013">
                  <Activity className="w-4 h-4 text-slate-500" data-id="element-1014" />
                  Moeilijkheidsgraad
                </h4>
                <div className="space-y-2.5" data-id="element-1015">
                  {byTier.map(tier => <div key={tier.tierRange} data-id="element-1016">
                      <div className="flex justify-between text-xs mb-1" data-id="element-1017">
                        <span className="font-medium text-slate-600" data-id="element-1018">
                          Tier {tier.tierRange}
                        </span>
                        <span className="text-slate-400" data-id="element-1019">
                          {tier.accuracy}% ({tier.correctCount}/
                          {tier.totalQuestions})
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-1020">
                        <div className="h-full bg-indigo-400 rounded-full transition-all" style={{
                  width: `${tier.accuracy}%`
                }} data-id="element-1021" />
                      </div>
                    </div>)}
                </div>
              </Card>}

            <Card className="p-4 shadow-sm" data-id="element-1022">
              <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm mb-3" data-id="element-1023">
                <Clock className="w-4 h-4 text-slate-500" data-id="element-1024" />
                Tijdsbesteding
              </h4>
              <div className="space-y-4" data-id="element-1025">
                <div className="flex items-center justify-between" data-id="element-1026">
                  <span className="text-slate-600 text-sm" data-id="element-1027">Totale tijd</span>
                  <span className="font-bold text-slate-900" data-id="element-1028">
                    {formatTime(time.totalSeconds)}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1029">
                  <span className="text-slate-600 text-sm" data-id="element-1030">Per vraag</span>
                  <span className="font-bold text-slate-900" data-id="element-1031">
                    {Math.round(time.avgPerQuestion)}s
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1032">
                  <span className="text-slate-600 text-sm" data-id="element-1033">Tempo</span>
                  <span className={`font-semibold px-2.5 py-0.5 rounded-full text-xs ${time.avgPerQuestion < 20 ? 'bg-emerald-100 text-emerald-700' : time.avgPerQuestion > 50 ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-1034">
                    {time.avgPerQuestion < 20 ? 'Vlot' : time.avgPerQuestion > 50 ? 'Rustig' : 'Gemiddeld'}
                  </span>
                </div>
              </div>
            </Card>
          </motion.section>}

        {/* 4. Strengths & Focus Areas */}
        {hasAnalysis && <motion.section {...fadeUp} transition={stagger(3)} className="space-y-3" data-id="element-1035">
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-1036">
              <Target className="w-4.5 h-4.5 text-slate-500" data-id="element-1037" />
              Inhoudelijke Analyse
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1038">
              {/* Strengths */}
              <Card className="p-4 shadow-sm border-emerald-100/60 bg-emerald-50/20" data-id="element-1039">
                <h4 className="flex items-center gap-2 font-bold text-emerald-800 text-sm mb-3" data-id="element-1040">
                  <Star className="w-4 h-4 text-emerald-500 fill-emerald-500" data-id="element-1041" />
                  Sterke Punten
                </h4>
                {strongAreas.length > 0 && <p className="text-xs text-emerald-600 mb-3 font-medium" data-id="element-1042">
                    {strongAreas.join(', ')}
                  </p>}
                {strengthsBySkill.length > 0 ? <ul className="space-y-2" data-id="element-1043">
                    {strengthsBySkill.slice(0, 3).flatMap(area => area.skills.slice(0, 2).map(skill => <li key={`${area.moduleId}-${skill.skillName}`} className="flex items-start gap-2" data-id="element-1044">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-1045" />
                          <div className="min-w-0" data-id="element-1046">
                            <span className="font-semibold text-emerald-900 text-xs capitalize" data-id="element-1047">
                              {skill.skillName}
                            </span>
                            <span className="text-emerald-500 text-xs ml-1.5" data-id="element-1048">
                              {Math.round(skill.avgAccuracy)}%
                            </span>
                          </div>
                        </li>))}
                  </ul> : <p className="text-emerald-500/70 text-xs italic" data-id="element-1049">
                    Nog geen specifieke sterke punten.
                  </p>}
              </Card>

              {/* Focus Areas */}
              <Card className="p-4 shadow-sm border-amber-100/60 bg-amber-50/20" data-id="element-1050">
                <h4 className="flex items-center gap-2 font-bold text-amber-800 text-sm mb-3" data-id="element-1051">
                  <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-1052" />
                  Aandachtspunten
                </h4>
                {weakAreas.length > 0 && <p className="text-xs text-amber-600 mb-3 font-medium" data-id="element-1053">
                    {weakAreas.join(', ')}
                  </p>}
                {focusAreasBySkill.length > 0 ? <ul className="space-y-2" data-id="element-1054">
                    {focusAreasBySkill.slice(0, 3).flatMap(area => area.skills.slice(0, 2).map(skill => <li key={`${area.moduleId}-${skill.skillName}`} className="flex items-start gap-2" data-id="element-1055">
                          <TrendingUp className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-1056" />
                          <div className="min-w-0" data-id="element-1057">
                            <span className="font-semibold text-amber-900 text-xs capitalize" data-id="element-1058">
                              {skill.skillName}
                            </span>
                            <span className="text-amber-500 text-xs ml-1.5" data-id="element-1059">
                              {Math.round(skill.avgAccuracy)}%
                            </span>
                          </div>
                        </li>))}
                  </ul> : <p className="text-amber-500/70 text-xs italic" data-id="element-1060">
                    Geen specifieke aandachtspunten.
                  </p>}
              </Card>
            </div>
          </motion.section>}

        {/* 5. Action Plan & Recommendations */}
        {(hasActionPlan || recommendation) && <motion.section {...fadeUp} transition={stagger(4)} className="space-y-3" data-id="element-1061">
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-1062">
              <Lightbulb className="w-4.5 h-4.5 text-slate-500" data-id="element-1063" />
              Advies & Actieplan
            </h3>

            <Card className="p-5 shadow-sm space-y-5" data-id="element-1064">
              {recommendation && <p className="text-slate-700 text-sm font-medium leading-relaxed" data-id="element-1065">
                  {recommendation}
                </p>}

              {/* Begin Met */}
              {actionPlan.beginMet && <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1066">
                  <div className="min-w-0" data-id="element-1067">
                    <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1068">
                      <Zap className="w-3.5 h-3.5 text-orange-500" data-id="element-1069" />
                      <span className="text-[10px] font-bold text-orange-700 uppercase tracking-wider" data-id="element-1070">
                        Prioriteit
                      </span>
                    </div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-1071">
                      {actionPlan.beginMet.familyDescription}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1072">
                      {actionPlan.beginMet.moduleTitle} ›{' '}
                      {actionPlan.beginMet.skillName}
                    </div>
                  </div>
                  {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="sm" className="bg-orange-500 hover:bg-orange-600 whitespace-nowrap flex-shrink-0" data-id="element-1073">
                      Oefenen
                    </Button>}
                </div>}

              {/* Ga Verder Met */}
              {actionPlan.gaVerderMet.length > 0 && <div data-id="element-1074">
                  <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1075">
                    Daarna verder met
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1076">
                    {actionPlan.gaVerderMet.map((item, idx) => <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 flex justify-between items-center" data-id="element-1077">
                        <div className="min-w-0" data-id="element-1078">
                          <div className="font-semibold text-slate-800 text-xs truncate" data-id="element-1079">
                            {item.familyDescription}
                          </div>
                          <div className="text-[10px] text-slate-400" data-id="element-1080">
                            {item.moduleTitle}
                          </div>
                        </div>
                        {onStartPracticeForModule && <button onClick={() => onStartPracticeForModule(item.moduleId)} className="p-1.5 text-slate-300 hover:text-orange-500 transition-colors flex-shrink-0" aria-label={`Oefen ${item.familyDescription}`} data-id="element-1081">
                            <Play className="w-3.5 h-3.5" data-id="element-1082" />
                          </button>}
                      </div>)}
                  </div>
                </div>}

              {/* Uitdaging */}
              {actionPlan.uitdaging && <div className="bg-sky-50 border border-sky-100 rounded-xl p-4" data-id="element-1083">
                  <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1084">
                    <Sparkles className="w-3.5 h-3.5 text-sky-500" data-id="element-1085" />
                    <span className="text-[10px] font-bold text-sky-700 uppercase tracking-wider" data-id="element-1086">
                      Extra Uitdaging
                    </span>
                  </div>
                  <div className="font-bold text-slate-900 text-sm" data-id="element-1087">
                    {actionPlan.uitdaging.suggestion}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-1088">
                    {actionPlan.uitdaging.moduleTitle}
                  </div>
                </div>}
            </Card>
          </motion.section>}

        {/* 6. Bottom Actions */}
        <motion.section {...fadeUp} transition={stagger(5)} className="pt-4 pb-6 flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-1089">
          {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 shadow-sm" data-id="element-1090">
              <BookOpen className="w-4 h-4 mr-2" data-id="element-1091" />
              Start met Oefenen
            </Button>}
          {onRetake && <Button onClick={onRetake} variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-1092">
              Opnieuw maken
            </Button>}
          <Button onClick={onExit} variant="ghost" size="lg" className="w-full sm:w-auto text-slate-500" data-id="element-1093">
            Terug naar Dashboard
          </Button>
        </motion.section>
      </main>
    </div>;
}